import type { RatingConfig } from "@squaredr/fieldcraft-core";
import type { FieldProps } from "../../registry/field-registry";
import { FieldWrapper } from "./FieldWrapper";
import { Button } from "../ui/button";
import { cn } from "../../utils/cn";

const ICON_PATHS: Record<string, string> = {
  star: "M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z",
  heart: "M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z",
  circle: "M12 2a10 10 0 1 0 0 20 10 10 0 0 0 0-20z",
};

export function RatingField({ field, value, error, touched, disabled, readonly, onChange, onBlur, onFocus }: FieldProps) {
  const config = field.config as RatingConfig | undefined;
  const max = config?.max ?? 5;
  const current = (value as number) ?? 0;
  const iconPath = ICON_PATHS[config?.icon ?? "star"] ?? ICON_PATHS.star;
  const showLabels = config?.showLabels === true;

  function handleKeyDown(e: React.KeyboardEvent<HTMLDivElement>) {
    if (disabled || readonly) return;
    let next = current;
    switch (e.key) {
      case "ArrowRight":
      case "ArrowUp":
        next = Math.min(current + 1, max);
        break;
      case "ArrowLeft":
      case "ArrowDown":
        next = Math.max(current - 1, 1);
        break;
      case "Home":
        next = 1;
        break;
      case "End":
        next = max;
        break;
      default:
        return;
    }
    e.preventDefault();
    onChange(next);
  }

  return (
    <FieldWrapper field={field} error={error} touched={touched}>
      <div
        className="flex gap-1"
        role="radiogroup"
        aria-label={field.label}
        onFocus={onFocus}
        onKeyDown={handleKeyDown}
      >
        {Array.from({ length: max }, (_, i) => {
          const n = i + 1;
          const selected = n <= current;
          return (
            <div key={n} className="flex flex-col items-center gap-0.5">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                tabIndex={n === (current || 1) ? 0 : -1}
                className={cn(
                  "h-9 w-9 p-0 rounded-md transition-colors",
                  selected ? "text-primary hover:text-primary/90" : "text-muted-foreground hover:text-foreground",
                )}
                disabled={disabled || readonly}
                onClick={() => { onChange(n); onBlur(); }}
                aria-label={`${n} of ${max}`}
                aria-checked={n === current}
                role="radio"
              >
                <svg viewBox="0 0 24 24" fill={selected ? "currentColor" : "none"} stroke="currentColor" strokeWidth={1.5} width={24} height={24}>
                  <path d={iconPath} />
                </svg>
              </Button>
              {showLabels && (
                <span className="text-[10px] text-muted-foreground">{n}</span>
              )}
            </div>
          );
        })}
      </div>
    </FieldWrapper>
  );
}
